// Home screen - Landing page
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card } from '../components';
import { getCompetitionIdsWithTokens, getLastVisitedCompetition } from '../utils/localStorage';
import { getCompetition } from '../services/competitionService';
import { getActivePlayers } from '../services/playerService';
import { signInWithEmail, signInWithGoogle, signOut, resendVerificationEmail } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';

const Home = () => {
  const navigate = useNavigate();
  const { user, isSuperuser } = useAuth();
  const [savedCompetitionIds, setSavedCompetitionIds] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendSuccess, setResendSuccess] = useState('');

  // No auto-redirect - let users choose which competition to access

  // Handle sign out from URL parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shouldSignOut = urlParams.get('signout');

    if (shouldSignOut === 'true') {
      // Sign out and clear the URL parameter
      signOut().then(() => {
        window.history.replaceState({}, '', '/');
        window.location.reload();
      }).catch(error => {
        console.error('Error signing out:', error);
      });
    }
  }, []);

  useEffect(() => {
    // Only load saved competition IDs if user has email (not anonymous)
    if (user && user.email) {
      const ids = getCompetitionIdsWithTokens();
      setSavedCompetitionIds(ids);
    } else {
      setSavedCompetitionIds([]);
      setCompetitions([]); // Clear competitions when user logs out
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    const loadCompetitions = async () => {
      if (savedCompetitionIds.length === 0) {
        setLoading(false);
        return;
      }

      setLoading(true);
      const competitionsData = await Promise.all(
        savedCompetitionIds.map(async (compId) => {
          try {
            const competition = await getCompetition(compId);
            const players = await getActivePlayers(compId);
            return {
              id: compId,
              name: competition?.name || 'Unknown Competition',
              playerCount: players.length,
              status: competition?.status || 'unknown'
            };
          } catch (error) {
            console.error('Error loading competition:', compId, error);
            return null;
          }
        })
      );
      setCompetitions(competitionsData.filter(c => c !== null));
      setLoading(false);
    };

    loadCompetitions();
  }, [savedCompetitionIds]);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoggingIn(true);

    try {
      await signInWithEmail(loginEmail, loginPassword);
      // After successful login, reload to trigger auto-redirect
      window.location.reload();
    } catch (error) {
      setLoginError(error.message);
      setLoggingIn(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoginError('');
    setLoggingIn(true);

    try {
      await signInWithGoogle();
      // After successful login, reload to trigger auto-redirect
      window.location.reload();
    } catch (error) {
      setLoginError(error.message);
      setLoggingIn(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      // Reload to show anonymous state
      window.location.reload();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleResendVerification = async () => {
    setResendingEmail(true);
    setResendSuccess('');
    setLoginError('');

    try {
      await resendVerificationEmail();
      setResendSuccess('Verification email sent! Check your inbox.');
      setTimeout(() => setResendSuccess(''), 5000);
    } catch (error) {
      setLoginError(error.message);
    } finally {
      setResendingEmail(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <img
              src="/bragz-logo.png"
              alt="Bragz Logo"
              className="w-80 h-auto"
            />
          </div>
          <p className="mt-4 text-lg italic text-gray-700">
            Track scores. Claim bragging rights.
          </p>
          <p className="mt-3 text-sm text-gray-600 max-w-sm mx-auto">
            A private multi-day scoreboard for friends, with category and overall winners.
          </p>
        </div>

        <Card>
          {/* Show Login first if not logged in */}
          {!user?.email ? (
            <div className="space-y-4">
              <p className="text-center text-gray-600 text-sm">
                Sign in to create or join a league
              </p>

              {loginError && (
                <div className="p-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs">
                  {loginError}
                </div>
              )}

              {/* Google Sign-In Button */}
              <button
                onClick={handleGoogleLogin}
                disabled={loggingIn}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-gray-700"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {loggingIn ? 'Signing in...' : 'Sign in with Google'}
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-2 bg-white text-gray-500">Or continue with email</span>
                </div>
              </div>

              {/* Email/Password Form */}
              <form onSubmit={handleEmailLogin} className="space-y-3">
                <div>
                  <input
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    required
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Email address"
                  />
                </div>
                <div>
                  <input
                    type="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    required
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Password"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loggingIn}
                  fullWidth
                >
                  {loggingIn ? 'Signing in...' : 'Sign In with Email'}
                </Button>
              </form>

              <p className="text-center text-xs text-gray-500">
                Don't have an account? <button onClick={() => navigate('/register')} className="text-blue-600 hover:text-blue-700 font-medium">Create one</button>
              </p>
            </div>
          ) : (
            <>
              {/* Logged in - Show League options */}
              <div className="space-y-3">
                <Button fullWidth onClick={() => navigate('/leagues?create=true')}>
                  Create New League
                </Button>
                <Button fullWidth variant="outline" onClick={() => navigate('/leagues')}>
                  My Leagues
                </Button>
              </div>

              {/* User info and sign out */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-600">
                    Signed in as: <span className="font-medium">{user.displayName || user.email}</span>
                    {user.emailVerified && <span className="ml-1 text-green-600">✓</span>}
                  </p>
                  <button
                    onClick={handleSignOut}
                    className="text-xs text-red-600 hover:text-red-700 underline"
                  >
                    Sign Out
                  </button>
                </div>

                {!user.emailVerified && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-xs text-yellow-800 mb-2">
                      Your email is not verified. Please check your inbox and verify your email to access all features.
                    </p>
                    {resendSuccess && (
                      <p className="text-xs text-green-600 mb-2">{resendSuccess}</p>
                    )}
                    {loginError && (
                      <p className="text-xs text-red-600 mb-2">{loginError}</p>
                    )}
                    <button
                      onClick={handleResendVerification}
                      disabled={resendingEmail}
                      className="text-xs text-blue-600 hover:text-blue-700 underline disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {resendingEmail ? 'Sending...' : 'Resend verification email'}
                    </button>
                  </div>
                )}
              </div>

              {/* Recent Competitions */}
              {competitions.length > 0 && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Recent Competitions</h3>
                  {loading ? (
                    <p className="text-sm text-gray-500">Loading...</p>
                  ) : (
                    <div className="space-y-2">
                      {competitions.map(comp => (
                        <button
                          key={comp.id}
                          onClick={() => navigate(`/competition/${comp.id}`)}
                          className="w-full text-left px-4 py-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900">{comp.name}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                {comp.playerCount} {comp.playerCount === 1 ? 'player' : 'players'}
                              </p>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded capitalize ${
                              comp.status === 'live' ? 'bg-green-100 text-green-800' :
                              comp.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                              comp.status === 'finished' ? 'bg-gray-100 text-gray-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {comp.status}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </Card>

        <div className="mt-8 text-center text-sm text-gray-500">
          <p>What are you waiting for? Let's brag.</p>
        </div>

        {/* Superuser Link */}
        {isSuperuser && (
          <div className="mt-4 text-center">
            <Link
              to="/superadmin"
              className="text-xs text-red-600 hover:text-red-700 font-medium"
            >
              Super Admin Panel
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;
